import { useQuery } from '@tanstack/react-query'
import { differenceInDays, differenceInMonths } from 'date-fns'
import { CURRENT_TIMEZONE } from '~/helpers/dateHelper'
import { useTRPC } from '~/services/trpc'

import type {
  AnalyticsQuery,
  AnalyticsQueryAggregate,
  AnalyticsQueryTimespan,
} from '@hap/analytics'

type AutoSeries = 'auto' | AnalyticsQuery['series']

export function parseSeries(
  timespan: Omit<AnalyticsQueryTimespan, 'timezone'>,
  series?: AutoSeries,
): AnalyticsQuery['series'] {
  if (series === 'auto') {
    if (differenceInDays(new Date(timespan.end), new Date(timespan.start)) <= 3) {
      return 'hour'
    } else if (differenceInMonths(new Date(timespan.end), new Date(timespan.start)) > 3) {
      return 'month'
    } else {
      return 'day'
    }
  }
  return series
}

export const useAnalytics = <Q extends QueryConfig>(
  props: Q,
  { enabled = true } = {},
): Response<Q['aggregations']> => {
  const trpc = useTRPC()
  const { data, isLoading } = useQuery(
    trpc.analytics.run.queryOptions(
      {
        ...props,
        timespan: {
          start:
            typeof props.timespan.start === 'string'
              ? props.timespan.start
              : props.timespan.start.toISOString(),
          end:
            typeof props.timespan.end === 'string'
              ? props.timespan.end
              : props.timespan.end.toISOString(),
          timezone:
            'timezone' in props.timespan ? props.timespan.timezone : CURRENT_TIMEZONE,
        },
        aggregations: Object.entries(props.aggregations).map(([key, value]) => ({
          ...value,
          alias: key,
        })),
        series: parseSeries(props.timespan, props.series),
      },
      {
        enabled,
        trpc: {
          context: {
            skipBatch: true,
          },
        },
      },
    ),
  )

  return {
    data: data,
    loading: isLoading,
  } as Response<Q['aggregations']>
}

export interface QueryConfig extends Omit<
  AnalyticsQuery,
  'series' | 'aggregations' | 'timespan'
> {
  series?: AutoSeries
  aggregations: Record<string, AnalyticsQueryAggregate>
  timespan: AnalyticsQueryTimespan | { start: Date | string; end: Date | string }
}

interface Response<A extends Record<string, AnalyticsQueryAggregate>> {
  data: Array<
    DataRowWithPercent<A> &
      DataRowWithPercentChange<A> &
      DataRow<A> & { time: string } & DataRowWithHydratedRecords<A>
  > | null
  loading: boolean
}

type SanitizeKey<S> = S extends string ? S : never
type RemoveId<S> = S extends `${infer Prefix}Id` ? Prefix : S

type DataRow<A extends Record<string, AnalyticsQueryAggregate>> = {
  -readonly [K in keyof A]: string
}

type DataRowWithPercent<A extends Record<string, AnalyticsQueryAggregate>> = {
  -readonly [K in keyof A as A[K] extends { op: 'count' }
    ? `${SanitizeKey<K>}_percent`
    : never]: number
}

type DataRowWithPercentChange<A extends Record<string, AnalyticsQueryAggregate>> = {
  -readonly [K in keyof A as A[K] extends { trends: true }
    ? `${SanitizeKey<K>}_percent_change`
    : never]: number
}

type DataRowWithHydratedRecords<A extends Record<string, AnalyticsQueryAggregate>> = {
  -readonly [K in keyof A as A[K] extends { hydrate: true }
    ? RemoveId<K>
    : never]: Record<string, string>
}
